/**
 * /comparisons — list saved DIFF-2 comparisons + create a new one.
 *
 * A comparison takes two estimates (ESX/PDF) for the same claim and splits
 * every line item into AGREED vs DIFFERENT, so a public adjuster can prep a
 * supplement without re-reading both estimates line by line.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, FileDiff, Loader2, Trash2, Upload } from 'lucide-react';

import { ComparisonsAPI } from '../lib/comparisonsApi';
import type { ComparisonAccess, ComparisonListItem, ComparisonJobStatus } from '../lib/comparisonsApi';
import { BackendAPI } from '../lib/api';
import type { Project } from '../types';
import { getErrorMessage, getProjectDisplayName } from '../lib/utils';

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

const ComparisonsPage: React.FC = () => {
  const navigate = useNavigate();

  const [comparisons, setComparisons] = useState<ComparisonListItem[]>([]);
  const [loading, setLoading] = useState(true);
  // PRP-MCP-10: who may compare. null until it loads (or if it cannot be read: the
  // server still enforces the gate, so the page then behaves as before).
  const [access, setAccess] = useState<ComparisonAccess | null>(null);
  const [accessLoaded, setAccessLoaded] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const [baselineFile, setBaselineFile] = useState<File | null>(null);
  const [comparisonFile, setComparisonFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  // Optional project to link the comparison to (B-629). Populated from the
  // user's existing projects; passed through to the create call as project_id.
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectId, setProjectId] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [progress, setProgress] = useState<ComparisonJobStatus | null>(null);

  // Per-row delete: which row is awaiting confirm, which is in-flight (B-686).
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const loadAccess = useCallback(async () => {
    try {
      setAccess(await ComparisonsAPI.access());
    } catch {
      setAccess(null);
    } finally {
      setAccessLoaded(true);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setListError(null);
    try {
      const list = await ComparisonsAPI.list();
      setComparisons(list);
    } catch (err) {
      setListError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAccess();
  }, [loadAccess]);

  useEffect(() => {
    if (!accessLoaded) return;
    if (access?.state === 'free') {
      setLoading(false);
      return;
    }
    void load();
  }, [load, accessLoaded, access?.state]);

  // Load the user's projects for the optional "link to project" picker (B-629).
  // Best-effort: a failure just leaves the picker empty, never blocks comparing.
  useEffect(() => {
    let active = true;
    BackendAPI.getProjects()
      .then((list) => {
        if (active) setProjects(list);
      })
      .catch(() => {
        /* non-fatal — comparisons still work without a linked project */
      });
    return () => {
      active = false;
    };
  }, []);

  const projectNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of projects) map.set(p.id, getProjectDisplayName(p));
    return map;
  }, [projects]);

  const handleCreate = useCallback(async () => {
    if (!baselineFile || !comparisonFile) return;
    setCreating(true);
    setCreateError(null);
    setProgress(null);
    try {
      // Async: the heavy per-line matching runs server-side in the background,
      // so the request returns immediately (no gateway timeout on big PDFs). We
      // poll the job for per-phase progress and navigate when it's done.
      const { job_id } = await ComparisonsAPI.createJobFromUpload(
        baselineFile,
        comparisonFile,
        name.trim() || undefined,
        projectId || undefined,
      );
      const MAX_POLLS = 600; // ~12 min ceiling
      let settled = false;
      for (let i = 0; i < MAX_POLLS; i += 1) {
        await new Promise((r) => setTimeout(r, 1200));
        let job: ComparisonJobStatus;
        try {
          job = await ComparisonsAPI.getJobStatus(job_id);
        } catch {
          continue; // transient poll hiccup — keep waiting
        }
        setProgress(job);
        if (job.status === 'done' && job.comparison_id) {
          navigate(`/comparisons/${job.comparison_id}`);
          return;
        }
        if (job.status === 'error') {
          setCreateError(job.error || 'Comparison failed while processing.');
          settled = true;
          break;
        }
      }
      if (!settled) {
        setCreateError('Still processing — check the Comparisons list in a moment.');
      }
    } catch (err) {
      setCreateError(getErrorMessage(err));
      void loadAccess(); // a 402 at the cap: show the used/cap line and the support message
    } finally {
      setCreating(false);
    }
  }, [loadAccess, baselineFile, comparisonFile, name, projectId, navigate]);

  const handleDelete = useCallback(async (id: string) => {
    setDeletingId(id);
    setDeleteError(null);
    try {
      await ComparisonsAPI.delete(id);
      setConfirmDeleteId(null);
      // Drop the row locally so the list updates without a full refetch.
      setComparisons((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      setDeleteError(getErrorMessage(err));
    } finally {
      setDeletingId(null);
    }
  }, []);

  const overallPct = progress
    ? Math.min(
        100,
        Math.round(
          (((progress.phase_index > 0 ? progress.phase_index - 1 : 0) +
            (progress.total > 0 ? progress.current / progress.total : 0)) /
            Math.max(1, progress.phase_total)) *
            100,
        ),
      )
    : 0;

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center text-sm font-medium text-muted-foreground">
        <Link to="/" className="hover:text-primary">Dashboard</Link>
        <ChevronRight className="mx-2 h-4 w-4" />
        <span className="text-foreground">Comparisons</span>
      </nav>

      <div>
        <h2 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
          <FileDiff className="h-6 w-6 text-primary" />
          Comparisons
        </h2>
        <p className="text-muted-foreground">
          Compare two estimates for the same claim — every line item split into what's
          agreed and what's different, for fast supplement prep.
        </p>
      </div>

      {access?.state === 'free' && (
        <div className="space-y-2 rounded-lg border bg-card p-6 shadow-sm" data-testid="comparisons-locked">
          <p className="text-sm text-foreground">
            Comparisons are included with Pro, Pro Plus and the trial.
          </p>
          <p className="flex flex-wrap gap-4 text-sm font-medium">
            <Link to={access.subscribe_url} className="text-primary underline">Subscribe</Link>
            <Link to={access.trial_url} className="text-primary underline">Start the trial</Link>
          </p>
        </div>
      )}

      {access?.state === 'entitled' && (
        <p className="text-sm text-muted-foreground" data-testid="comparisons-usage">
          {access.used} of {access.cap} included comparisons used.
        </p>
      )}

      {access?.state === 'entitled' && access.message && (
        <div
          className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/15 dark:text-amber-200"
          role="status"
          data-testid="comparisons-cap"
        >
          {access.message}
        </div>
      )}

      {/* New comparison (hidden for a free account and at the cap) */}
      {access?.state !== 'free' && !(access?.state === 'entitled' && access.message) && (
      <div className="space-y-4 rounded-lg border bg-card p-6 shadow-sm">
        <h3 className="text-sm font-semibold text-foreground">New comparison</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground" htmlFor="baseline-file">
              Estimate A (baseline)
            </label>
            <input
              id="baseline-file"
              type="file"
              accept=".esx,.pdf"
              onChange={(e) => setBaselineFile(e.target.files?.[0] ?? null)}
              className="block w-full text-sm text-foreground file:mr-3 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-primary/90"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground" htmlFor="comparison-file">
              Estimate B (comparison)
            </label>
            <input
              id="comparison-file"
              type="file"
              accept=".esx,.pdf"
              onChange={(e) => setComparisonFile(e.target.files?.[0] ?? null)}
              className="block w-full text-sm text-foreground file:mr-3 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-primary/90"
            />
          </div>
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground" htmlFor="comparison-name">
            Name (optional)
          </label>
          <input
            id="comparison-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Smith claim — carrier vs. our estimate"
            className="w-full rounded-md border px-3 py-2 text-sm"
          />
        </div>
        {projects.length > 0 && (
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground" htmlFor="comparison-project">
              Link to project (optional)
            </label>
            <select
              id="comparison-project"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              className="w-full rounded-md border px-3 py-2 text-sm"
            >
              <option value="">No project</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {getProjectDisplayName(p)}
                </option>
              ))}
            </select>
          </div>
        )}
        {createError && (
          <div className="rounded-md border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/15 p-3 text-sm text-red-800 dark:text-red-300">
            {createError}
          </div>
        )}

        {creating && progress && (
          <div className="space-y-2 rounded-md border bg-muted p-3" role="status" aria-live="polite">
            <div className="flex items-center justify-between text-xs">
              <span className="font-medium text-foreground">
                {progress.phase_label || 'Starting…'}
              </span>
              <span className="text-muted-foreground">
                Step {Math.max(1, progress.phase_index)} of {progress.phase_total}
              </span>
            </div>
            {/* Overall progress across phases */}
            <div className="h-1.5 w-full overflow-hidden rounded bg-muted">
              <div
                className="h-full rounded bg-primary transition-all"
                style={{ width: `${overallPct}%` }}
              />
            </div>
            {/* Within-phase: per-line for matching, indeterminate otherwise */}
            {progress.total > 0 ? (
              <div className="space-y-1">
                <div className="h-2 w-full overflow-hidden rounded bg-muted">
                  <div
                    className="h-full rounded bg-sky-500 transition-all"
                    style={{ width: `${Math.round((progress.current / progress.total) * 100)}%` }}
                  />
                </div>
                <p className="text-[11px] text-muted-foreground">
                  {progress.current} / {progress.total} lines matched
                </p>
              </div>
            ) : (
              progress.status !== 'done' && (
                <div className="h-2 w-full overflow-hidden rounded bg-muted">
                  <div className="h-full w-1/3 animate-pulse rounded bg-sky-400" />
                </div>
              )
            )}
          </div>
        )}

        <button
          type="button"
          onClick={handleCreate}
          disabled={!baselineFile || !comparisonFile || creating}
          className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {creating ? 'Comparing…' : 'Compare'}
        </button>
      </div>
      )}

      {/* Saved comparisons (not for a free account: the server refuses the list) */}
      {access?.state !== 'free' && (
      <div className="rounded-lg border bg-card shadow-sm">
        <div className="border-b px-4 py-3">
          <h3 className="text-sm font-semibold text-foreground">Saved comparisons</h3>
        </div>
        {listError && (
          <div className="p-4 text-sm text-red-700 dark:text-red-300">{listError}</div>
        )}
        {loading && (
          <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>
        )}
        {!loading && comparisons.length === 0 && !listError && (
          <div className="p-6 text-center text-sm text-muted-foreground">
            No comparisons yet. Create one above.
          </div>
        )}
        {deleteError && (
          <div className="border-b px-4 py-2 text-sm text-red-700">{deleteError}</div>
        )}
        {!loading && comparisons.length > 0 && (
          <ul className="divide-y">
            {comparisons.map((c) => (
              <li key={c.id} className="flex items-center gap-2 pr-2 transition-colors hover:bg-muted">
                <Link
                  to={`/comparisons/${c.id}`}
                  className="flex min-w-0 flex-1 flex-col gap-1 px-4 py-3 transition-colors sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-foreground">
                      {c.name || 'Untitled comparison'}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {c.baseline_source_ref || 'Estimate A'} vs. {c.comparison_source_ref || 'Estimate B'}
                    </div>
                    {c.project_id && (
                      <div className="mt-0.5 inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-600">
                        {projectNameById.get(c.project_id) || 'Linked project'}
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">{formatDate(c.created_at)}</div>
                </Link>
                {confirmDeleteId === c.id ? (
                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      type="button"
                      onClick={() => void handleDelete(c.id)}
                      disabled={deletingId === c.id}
                      className="flex items-center gap-1 rounded-md bg-red-600 px-2 py-1 text-xs font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-60"
                    >
                      {deletingId === c.id ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                      Delete
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmDeleteId(null)}
                      disabled={deletingId === c.id}
                      className="rounded-md border px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-slate-100"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    aria-label={`Delete ${c.name || 'comparison'}`}
                    title="Delete comparison"
                    onClick={() => {
                      setDeleteError(null);
                      setConfirmDeleteId(c.id);
                    }}
                    className="shrink-0 rounded-md p-2 text-muted-foreground transition-colors hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      )}
    </div>
  );
};

export default ComparisonsPage;
